import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, Card, CardContent, CardActions, Button, Grid } from '@mui/material';
import { QrCode2 as QrCodeIcon, Assessment as ReportsIcon } from '@mui/icons-material';
import { useAuthStore } from '../../authentication';
import { ShareQRCodeDropdown } from './ShareQRCodeDropdown';

interface QuickActionsWidgetProps {
  showQRCode?: boolean;
  showReports?: boolean;
}

/**
 * Quick Actions widget for dashboard
 * Displays QR code sharing and reports access cards
 */
export const QuickActionsWidget: React.FC<QuickActionsWidgetProps> = ({
  showQRCode = true,
  showReports = true,
}) => {
  const navigate = useNavigate();
  const { currentTenant } = useAuthStore();

  // Don't render if nothing to show
  if (!showQRCode && !showReports) {
    return null;
  }

  return (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={3}>
        {showQRCode && (
          <Grid item xs={12} sm={6}>
            <Card
              elevation={0}
              sx={(theme) => ({
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor:
                  theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.012), 0px 2px 8px rgba(0, 0, 0, 0.012)',
              })}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 3 }}>
                <QrCodeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" component="h3" gutterBottom>
                  Share QR Link
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generate QR code for your students to scan and respond to the survey
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                <ShareQRCodeDropdown variant="contained" size="large" />
              </CardActions>
            </Card>
          </Grid>
        )}

        {showReports && (
          <Grid item xs={12} sm={6}>
            <Card
              elevation={0}
              sx={(theme) => ({
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor:
                  theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.012), 0px 2px 8px rgba(0, 0, 0, 0.012)',
              })}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 3 }}>
                <ReportsIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" component="h3" gutterBottom>
                  View Recent Reports
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Access reports with charts, analytics, and PDF export options
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<ReportsIcon />}
                  size="large"
                  onClick={() => {
                    if (currentTenant?.id) {
                      navigate(`/app/manage/universities/${currentTenant.id}/reports`);
                    } else {
                      navigate('/app/reports');
                    }
                  }}
                >
                  View Reports
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
